import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import Logger from "../../services/Logger";
import Item from "../components/Item";
import WeeklyEmailService from "./WeeklyEmailService";
import {
    DEFAULT_WEEKLY_SUBJECT,
    WEEKLY_LOCALES,
    defaultWeeklyHtml,
    renderWeeklyDesign,
} from "./weeklyEmailDefaults";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { toPng } from "html-to-image";
import { downloadPngDataUrl } from "../welcome/downloadPngDataUrl";

const EMAIL_CAPTURE_W = 640;

function scopeEmailCss(css) {
    return String(css || "")
        .replace(/\bhtml\b/g, ".weekly-email-capture")
        .replace(/\bbody\b/g, ".weekly-email-capture");
}

function waitForImages(root) {
    const images = [...root.querySelectorAll("img")];
    return Promise.all(
        images.map(
            (img) =>
                new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                        return;
                    }
                    const done = () => resolve();
                    img.addEventListener("load", done, { once: true });
                    img.addEventListener("error", done, { once: true });
                    window.setTimeout(done, 4000);
                }),
        ),
    );
}

async function captureEmailPng(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const style = document.createElement("style");
    style.setAttribute("data-weekly-email-capture", "1");
    style.textContent = scopeEmailCss(
        [...parsed.querySelectorAll("style")].map((node) => node.textContent || "").join("\n"),
    );
    const mount = document.createElement("div");
    mount.className = "weekly-email-capture";
    mount.setAttribute("aria-hidden", "true");
    mount.style.cssText = [
        "position:relative",
        "left:auto",
        "top:auto",
        `width:${EMAIL_CAPTURE_W}px`,
        "box-sizing:border-box",
        "background:#ffffff",
        "z-index:auto",
    ].join(";");
    const holder = document.createElement("div");
    holder.setAttribute("aria-hidden", "true");
    holder.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        `width:${EMAIL_CAPTURE_W}px`,
        "overflow:visible",
        "pointer-events:none",
    ].join(";");
    mount.innerHTML = parsed.body?.innerHTML || "";
    mount.querySelectorAll("img").forEach((img) => {
        const src = img.getAttribute("src");
        if (!src) return;
        img.crossOrigin = "anonymous";
        img.setAttribute("src", src);
    });
    document.head.appendChild(style);
    holder.appendChild(mount);
    document.body.appendChild(holder);
    try {
        await waitForImages(mount);
        await new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        const width = Math.max(mount.scrollWidth, mount.offsetWidth, EMAIL_CAPTURE_W);
        const height = Math.max(mount.scrollHeight, mount.offsetHeight, 1);
        if (height < 40) {
            throw new Error("Email design is not ready");
        }
        return await toPng(mount, {
            cacheBust: true,
            pixelRatio: 2,
            skipFonts: true,
            width,
            height,
            backgroundColor: "#ffffff",
            onImageErrorHandler: () => undefined,
        });
    } finally {
        holder.remove();
        style.remove();
    }
}

export default function WeeklyEmailPanel() {
    const [locale, setLocale] = useState("en");
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [saved, setSaved] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [previewSubject, setPreviewSubject] = useState("");
    const [previewHtml, setPreviewHtml] = useState("");

    const applyModel = (data) => {
        setSubject(typeof data?.subject === "string" ? data.subject : "");
        setHtml(typeof data?.htmlContent === "string" ? data.htmlContent : "");
        setSaved(data?.saved === true);
        setUpdatedAt(data?.updatedAt ?? null);
    };

    const load = useCallback(async (nextLocale) => {
        setLoading(true);
        setError("");
        setMessage("");
        setPreviewSubject("");
        setPreviewHtml("");
        try {
            const data = await WeeklyEmailService.getTemplate(nextLocale);
            applyModel(data);
        } catch (err) {
            Logger.error("Weekly email template:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to load weekly email template");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(locale);
    }, [load, locale]);

    const handleSave = async () => {
        if (!subject.trim() || !html.trim() || busy) return;
        setBusy("save");
        setError("");
        setMessage("");
        try {
            const data = await WeeklyEmailService.save({ locale, subject, htmlContent: defaultWeeklyHtml() });
            applyModel(data);
            setMessage("Weekly email template saved. The weekly task uses this template.");
        } catch (err) {
            Logger.error("Save weekly email template:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to save weekly email template");
        } finally {
            setBusy("");
        }
    };

    const handlePreview = async () => {
        if (!subject.trim() || !html.trim() || busy) return;
        setBusy("preview");
        setError("");
        setMessage("");
        try {
            const data = await WeeklyEmailService.preview({ subject, htmlContent: defaultWeeklyHtml() });
            setPreviewSubject(typeof data?.subject === "string" ? data.subject : "");
            setPreviewHtml(typeof data?.html === "string" ? data.html : "");
        } catch (err) {
            Logger.error("Preview weekly email:", err);
            setPreviewSubject("");
            setPreviewHtml("");
            setError(typeof err === "string" ? err : err?.message || "Failed to preview weekly email");
        } finally {
            setBusy("");
        }
    };

    const handleDefaultTemplate = () => {
        setSubject(DEFAULT_WEEKLY_SUBJECT);
        setHtml(defaultWeeklyHtml());
        setPreviewSubject("");
        setPreviewHtml("");
        setMessage("");
        setError("");
    };

    const updatedLabel = updatedAt ? dayjs(Number(updatedAt)).format("YYYY-MM-DD HH:mm") : "";
    const designHtml = useMemo(() => {
        if (previewHtml) return previewHtml;
        return renderWeeklyDesign(defaultWeeklyHtml());
    }, [previewHtml]);

    const handleDownload = async () => {
        if (!designHtml || busy) return;
        setBusy("download");
        setError("");
        setMessage("");
        try {
            const dataUrl = await captureEmailPng(designHtml);
            await downloadPngDataUrl(dataUrl, "weekly-email-template.png");
        } catch (err) {
            Logger.error("Download weekly email image:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to download image");
        } finally {
            setBusy("");
        }
    };

    return (
        <Item>
            <Card>
                <CardContent>
                    <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
                        Weekly email template
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        English is the template for now. Other languages stay in the list for later.
                        Until a language is saved, the weekly task uses the English template. Puzzle
                        titles, images, and play links are filled in when the email goes out. The Word Wheel
                        block uses the latest Facebook share, and its link opens that puzzle on our site.
                    </Typography>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                    flexWrap: "wrap",
                                }}
                            >
                                <FormControl size="small" sx={{ width: 180, flexShrink: 0 }}>
                                    <InputLabel id="weekly-email-locale-label">Language</InputLabel>
                                    <Select
                                        labelId="weekly-email-locale-label"
                                        label="Language"
                                        value={locale}
                                        onChange={(e) => setLocale(e.target.value)}
                                    >
                                        {WEEKLY_LOCALES.map((item) => (
                                            <MenuItem key={item.value} value={item.value} disabled={!item.enabled}>
                                                {item.enabled ? item.label : `${item.label} (later)`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={{ flex: "1 1 280px" }}
                                    helperText="{{subjectPrefix}} is the localized weekly heading. {{puzzleSubject}} is this week's puzzle titles."
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {saved
                                    ? `Saved template${updatedLabel ? ` · updated ${updatedLabel}` : ""}`
                                    : "No saved template for this language."}
                            </Typography>
                            {message ? <Alert severity="success">{message}</Alert> : null}
                            {error ? <Alert severity="error">{error}</Alert> : null}
                            <Box
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    bgcolor: "#f4f4f5",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, pt: 1.5 }}>
                                    Design{previewSubject ? ` · ${previewSubject}` : ""}
                                </Typography>
                                {designHtml ? (
                                    <Box
                                        component="iframe"
                                        title="Weekly email design"
                                        srcDoc={designHtml}
                                        sandbox=""
                                        sx={{
                                            display: "block",
                                            width: "100%",
                                            height: 920,
                                            border: 0,
                                            bgcolor: "#ffffff",
                                        }}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 6 }}>
                                        Click Default template to open the email design.
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleSave}
                                    disabled={Boolean(busy) || !subject.trim() || !html.trim()}
                                    sx={{ textTransform: "none", minHeight: 44 }}
                                >
                                    {busy === "save" ? "Saving…" : "Save"}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handlePreview}
                                    disabled={Boolean(busy) || !subject.trim() || !html.trim()}
                                    sx={{ textTransform: "none", minHeight: 44 }}
                                >
                                    {busy === "preview" ? "Previewing…" : "Preview"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        busy === "download" ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <SaveAltIcon />
                                        )
                                    }
                                    onClick={handleDownload}
                                    disabled={Boolean(busy) || !designHtml}
                                    sx={{ textTransform: "none", minHeight: 44 }}
                                >
                                    {busy === "download" ? "Downloading…" : "Download"}
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={handleDefaultTemplate}
                                    disabled={Boolean(busy)}
                                    sx={{ textTransform: "none", minHeight: 44 }}
                                >
                                    Default template
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Preview fills this week&apos;s puzzles into the design. Nothing is sent.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Item>
    );
}
